import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Plus, Save, Lock as LockIcon, Users as UsersIcon, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI, employeesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function UsersManagementTab() {
  const { isAdmin, isManager } = useAuth();
  const managerOnly = !!isManager && !isAdmin;

  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSaving, setUsersSaving] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [roleEdits, setRoleEdits] = useState({});

  const [userForm, setUserForm] = useState({
    employee_id: '',
    name: '',
    email: '',
    password: '',
    role: 'operator',
  });

  const canSeeAdminManagerInDropdown = !managerOnly;

  const employeeById = useMemo(() => {
    const m = {};
    (employees || []).forEach(e => { m[e.id] = e; });
    return m;
  }, [employees]);

  const loadUsersAndEmployees = async () => {
    try {
      setUsersLoading(true);
      const [empsRes, usersRes] = await Promise.all([
        employeesAPI.getAll(),
        authAPI.getUsers(),
      ]);
      setEmployees(empsRes.data || []);
      setUsersList(usersRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar utilizadores/funcionários');
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadUsersAndEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createUserLogin = async () => {
    if (!userForm.email || !userForm.email.includes('@')) { toast.error('Email inválido'); return; }
    if (!userForm.password || userForm.password.length < 6) { toast.error('Password mínima: 6 caracteres'); return; }

    setUsersSaving(true);
    try {
      await authAPI.register({
        employee_id: userForm.employee_id || undefined,
        name: userForm.name || '',
        email: userForm.email,
        password: userForm.password,
        role: userForm.role,
      });
      toast.success('Utilizador criado');
      setUserForm({ employee_id: '', name: '', email: '', password: '', role: 'operator' });
      await loadUsersAndEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar utilizador');
    } finally {
      setUsersSaving(false);
    }
  };

  const resetUserPassword = async (email) => {
    const newPass = window.prompt(`Nova password para ${email}:`);
    if (!newPass) return;
    if (newPass.length < 6) { toast.error('Password mínima: 6 caracteres'); return; }

    setUsersSaving(true);
    try {
      await authAPI.resetPassword(email, newPass);
      toast.success('Password alterada');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao alterar password');
    } finally {
      setUsersSaving(false);
    }
  };

  const setUserPosPin = async (u) => {
    if (!u?.id) return;

    const pin = window.prompt(`Definir PIN POS para ${u.email}\n\nUse 4 a 6 dígitos:`);
    if (pin === null) return;

    const normalized = String(pin || '').replace(/\D+/g, '');
    if (normalized.length < 4 || normalized.length > 6) {
      toast.error('O PIN do POS deve ter 4 a 6 dígitos');
      return;
    }

    setUsersSaving(true);
    try {
      await authAPI.setPosPin(u.id, normalized);
      toast.success('PIN POS guardado');
      await loadUsersAndEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao guardar PIN POS');
    } finally {
      setUsersSaving(false);
    }
  };

  const toggleUserActive = async (u) => {
    if (!u?.id) return;
    setUsersSaving(true);
    try {
      await authAPI.updateUser(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Conta desativada' : 'Conta ativada');
      await loadUsersAndEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao atualizar utilizador');
    } finally {
      setUsersSaving(false);
    }
  };

  const updateUserRole = async (userId) => {
    const newRole = roleEdits?.[userId];
    if (!newRole) return;

    setUsersSaving(true);
    try {
      await authAPI.updateUser(userId, { role: newRole });
      toast.success('Role atualizado');
      await loadUsersAndEmployees();
      setRoleEdits(prev => {
        const copy = { ...(prev || {}) };
        delete copy[userId];
        return copy;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao atualizar role');
    } finally {
      setUsersSaving(false);
    }
  };

  return (
    <Card className="card-dark">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <UsersIcon className="w-5 h-5 text-emerald-400" />
          Gestão de Utilizadores
        </CardTitle>
        <CardDescription>
          Criar acessos e permissões (Admin/Manager). Primeiro escolhe o funcionário RH, depois cria o login.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <Button
          onClick={loadUsersAndEmployees}
          variant="outline"
          className="border-slate-700"
          disabled={usersLoading || usersSaving}
        >
          {usersLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Atualizar listas
        </Button>

        <div className="p-4 rounded-lg bg-slate-800/40 border border-slate-700 space-y-4">
          <h3 className="text-white font-semibold">Criar login</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300">Funcionário RH</Label>
              <Select
                value={userForm.employee_id}
                onValueChange={(val) => {
                  const emp = employeeById[val];
                  setUserForm(prev => ({
                    ...prev,
                    employee_id: val,
                    name: emp?.name || prev.name,
                    email: emp?.email || prev.email,
                  }));
                }}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue placeholder={usersLoading ? "A carregar..." : "Escolhe um funcionário"} />
                </SelectTrigger>
                <SelectContent>
                  {(employees || []).map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.email ? `(${e.email})` : ''} [{String(e.id).slice(0, 6)}]
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Dica: se o funcionário RH não tiver email, podes escrever manualmente.
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Nome</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-900 border-slate-700"
              />
            </div>

            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-900 border-slate-700"
              />
            </div>

            <div>
              <Label className="text-slate-300">Role</Label>
              <Select
                value={userForm.role}
                onValueChange={(val) => setUserForm(prev => ({ ...prev, role: val }))}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">operator (caixa)</SelectItem>
                  <SelectItem value="staff">staff (loja)</SelectItem>
                  {canSeeAdminManagerInDropdown && <SelectItem value="manager">manager (chefe)</SelectItem>}
                  {canSeeAdminManagerInDropdown && <SelectItem value="admin">admin (IT/dono)</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                className="bg-slate-900 border-slate-700"
                placeholder="mín. 6 caracteres"
              />
            </div>
          </div>

          <Button
            onClick={createUserLogin}
            disabled={usersSaving}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            {usersSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Criar utilizador
          </Button>
        </div>

        <div className="space-y-3">
          <h3 className="text-white font-semibold">Utilizadores existentes</h3>

          {usersLoading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              A carregar...
            </div>
          ) : (
            <div className="space-y-2">
              {(usersList || []).length === 0 ? (
                <p className="text-slate-500">Sem utilizadores para mostrar.</p>
              ) : (usersList || []).map(u => (
                <div key={u.id} className="p-3 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">
                      {u.name} <span className="text-slate-400">({u.role})</span>
                    </p>
                    <p className="text-slate-400 text-sm truncate">{u.email}</p>
                    <p className="text-slate-500 text-xs">
                      employee_id: {u.employee_id || '-'} | estado: {u.is_active ? 'ativo' : 'inativo'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-2">
                      <Select
                        value={(roleEdits?.[u.id] ?? u.role)}
                        onValueChange={(val) => setRoleEdits(prev => ({ ...(prev || {}), [u.id]: val }))}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-700 w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operator">operator</SelectItem>
                          <SelectItem value="staff">staff</SelectItem>
                          {canSeeAdminManagerInDropdown && <SelectItem value="manager">manager</SelectItem>}
                          {canSeeAdminManagerInDropdown && <SelectItem value="admin">admin</SelectItem>}
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        className="border-slate-700"
                        onClick={() => updateUserRole(u.id)}
                        disabled={usersSaving || (roleEdits?.[u.id] ?? u.role) === u.role}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </Button>
                    </div>

                    <Button
                      variant="outline"
                      className="border-slate-700"
                      onClick={() => resetUserPassword(u.email)}
                      disabled={usersSaving}
                    >
                      <LockIcon className="w-4 h-4 mr-2" />
                      Reset
                    </Button>

                    <Button
                      variant="outline"
                      className={u.is_active ? "border-rose-500/50 text-rose-400 hover:bg-rose-500/10" : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"}
                      onClick={() => toggleUserActive(u)}
                      disabled={usersSaving}
                    >
                      {u.is_active ? "Desativar" : "Ativar"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
